/**
 * BilingualEditor
 *
 * Renders TWO text fields side by side — Spanish (ES) and English (EN) —
 * with an optional "Suggest translation" button that calls the admin
 * translate endpoint to fill the empty side.
 *
 * Props:
 *   value:        { es: string, en: string }
 *   onChange:     (next: { es, en }) => void
 *   multiline:    boolean (default false) — renders <textarea> instead of <input>
 *   rows:         number of rows when multiline
 *   placeholderEs / placeholderEn
 *   testidPrefix: data-testid prefix (e.g. "rules-tiebreaker-0")
 *   compact:      tighter spacing (used inline in tables / cards)
 */
import React, { useState } from "react";
import { Languages, Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { toBilingual } from "@/lib/bilingual";
import { toast } from "sonner";

export default function BilingualEditor({
  value,
  onChange,
  multiline = false,
  rows = 4,
  placeholderEs = "",
  placeholderEn = "",
  testidPrefix = "bilingual",
  compact = false,
  className = "",
}) {
  const v = toBilingual(value);
  const [busy, setBusy] = useState(null); // null | "es2en" | "en2es"

  const setSide = (side, text) => onChange({ ...v, [side]: text });

  const suggest = async (from, to) => {
    const src = (v[from] || "").trim();
    if (!src) {
      toast.error(from === "es" ? "Escribe primero el texto en español" : "Write the English text first");
      return;
    }
    setBusy(`${from}2${to}`);
    try {
      const res = await api.post("/admin/translate", {
        text: src,
        from_lang: from,
        to_lang: to,
      });
      const translation = res.data?.translation || "";
      if (!translation) {
        toast.error("La IA devolvió una traducción vacía");
        return;
      }
      onChange({ ...v, [to]: translation });
      toast.success(to === "en" ? "Traducción al inglés sugerida" : "Traducción al español sugerida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo traducir");
    } finally {
      setBusy(null);
    }
  };

  const fieldClass = `w-full bg-black border border-zinc-800 px-3 py-2 outline-none focus:border-[var(--c-lime)] text-sm rounded-sm ${multiline ? "leading-relaxed" : ""}`;
  const InputTag = multiline ? "textarea" : "input";

  return (
    <div className={`space-y-2 ${className}`} data-testid={`${testidPrefix}-bilingual`}>
      <div className={`grid grid-cols-1 ${compact ? "" : "md:grid-cols-2"} gap-2`}>
        {/* ES side */}
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">🇪🇸 Español</span>
            <button
              type="button"
              onClick={() => suggest("es", "en")}
              disabled={busy === "es2en" || !(v.es || "").trim()}
              data-testid={`${testidPrefix}-suggest-en`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--c-lime)] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
              title="Sugerir traducción al inglés con IA"
            >
              {busy === "es2en" ? <Loader2 size={11} className="animate-spin" /> : <Languages size={11} />}
              <span>ES</span><ArrowRight size={10} /><span>EN</span>
            </button>
          </div>
          <InputTag
            value={v.es}
            onChange={(e) => setSide("es", e.target.value)}
            placeholder={placeholderEs}
            data-testid={`${testidPrefix}-es`}
            rows={multiline ? rows : undefined}
            className={fieldClass}
          />
        </div>
        {/* EN side */}
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">🇺🇸 English</span>
            <button
              type="button"
              onClick={() => suggest("en", "es")}
              disabled={busy === "en2es" || !(v.en || "").trim()}
              data-testid={`${testidPrefix}-suggest-es`}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[var(--c-lime)] hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
              title="Sugerir traducción al español con IA"
            >
              {busy === "en2es" ? <Loader2 size={11} className="animate-spin" /> : <Languages size={11} />}
              <span>EN</span><ArrowLeft size={10} /><span>ES</span>
            </button>
          </div>
          <InputTag
            value={v.en}
            onChange={(e) => setSide("en", e.target.value)}
            placeholder={placeholderEn}
            data-testid={`${testidPrefix}-en`}
            rows={multiline ? rows : undefined}
            className={fieldClass}
          />
        </div>
      </div>
    </div>
  );
}
